import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BadgeAlert, Clock3, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'completed') return 'bg-emerald-100 text-emerald-800';
  if (s === 'overdue') return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
}

function installmentTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid') return 'bg-emerald-100 text-emerald-800';
  if (s === 'overdue') return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

function Progress({ value }) {
  const safe = Math.max(0, Math.min(100, Number(value || 0)));
  return (
    <div className="space-y-1">
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${safe}%` }} />
      </div>
      <p className="text-xs text-slate-500">{safe.toFixed(0)}% paid</p>
    </div>
  );
}

export default function BudgetLoans() {
  const navigate = useNavigate();
  const { id: loanId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [paymentLoan, setPaymentLoan] = useState(null);
  const [form, setForm] = useState({
    branch_id: 'all',
    type: 'taken',
    lender_name: '',
    borrower_name: '',
    principal: '',
    interest_rate: '',
    interest_type: 'flat',
    duration_in_months: '',
    payment_frequency: 'monthly',
    start_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    collateral_description: '',
    collateral_value: '',
  });
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().slice(0, 16),
    notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = loanId ? `/budget/loans/${loanId}` : '/budget/loans';
      const res = await api.get(endpoint);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load loans');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId]);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const singleBranch = activeBranches.length <= 1 || !!data?.single_branch;
  const loans = Array.isArray(data?.loans) ? data.loans : [];
  const installments = Array.isArray(data?.installments) ? data.installments : [];
  const summary = data?.summary || {};
  const reports = data?.reports || {};
  const loan = data?.loan || null;
  const currency = user?.default_currency || loan?.currency || 'UGX';
  const canManage = !!data?.can_manage;

  const resetForm = () => {
    setForm({
      branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || '') : '',
      type: 'taken',
      lender_name: '',
      borrower_name: '',
      principal: '',
      interest_rate: '',
      interest_type: 'flat',
      duration_in_months: '',
      payment_frequency: 'monthly',
      start_date: new Date().toISOString().slice(0, 10),
      due_date: '',
      collateral_description: '',
      collateral_value: '',
    });
  };

  const openCreate = () => {
    setEditing(null);
    resetForm();
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      branch_id: item.branch_id || (singleBranch ? (activeBranches[0]?.id || branches[0]?.id || '') : ''),
      type: item.type || 'taken',
      lender_name: item.lender_name || '',
      borrower_name: item.borrower_name || '',
      principal: item.principal ?? '',
      interest_rate: item.interest_rate ?? '',
      interest_type: item.interest_type || 'flat',
      duration_in_months: item.duration_in_months ?? '',
      payment_frequency: item.payment_frequency || 'monthly',
      start_date: item.start_date ? String(item.start_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
      due_date: item.due_date ? String(item.due_date).slice(0, 10) : '',
      collateral_description: item.collateral_description || '',
      collateral_value: item.collateral_value ?? '',
    });
    setOpen(true);
  };

  const openPayment = (item) => {
    setPaymentLoan(item);
    setPaymentForm({
      amount: '',
      payment_date: new Date().toISOString().slice(0, 16),
      notes: '',
    });
    setPaymentOpen(true);
  };

  const saveLoan = async () => {
    if (!form.lender_name.trim() && !form.borrower_name.trim()) return toast.error('Enter the lender or borrower name.');
    if (Number(form.principal || 0) <= 0) return toast.error('Principal must be greater than zero.');
    setSaving(true);
    try {
      const payload = {
        ...form,
        branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || form.branch_id || 'all') : form.branch_id,
        principal: Number(form.principal || 0),
        interest_rate: Number(form.interest_rate || 0),
        duration_in_months: Number(form.duration_in_months || 1),
        collateral_value: Number(form.collateral_value || 0),
      };
      if (editing?.id) {
        await api.put(`/budget/loans/${editing.id}`, payload);
        toast.success('Loan updated');
      } else {
        await api.post('/budget/loans', payload);
        toast.success('Loan created');
      }
      setOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to save loan');
    } finally {
      setSaving(false);
    }
  };

  const savePayment = async () => {
    if (!paymentLoan) return;
    if (Number(paymentForm.amount || 0) <= 0) return toast.error('Payment amount must be greater than zero.');
    setSaving(true);
    try {
      await api.post(`/budget/loans/${paymentLoan.id}/payment`, {
        amount: Number(paymentForm.amount || 0),
        payment_date: paymentForm.payment_date,
        notes: paymentForm.notes,
      });
      toast.success('Loan payment recorded');
      setPaymentOpen(false);
      setPaymentLoan(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const deleteLoan = async (item) => {
    if (!window.confirm('Delete this loan? This will remove its installments too.')) return;
    try {
      await api.delete(`/budget/loans/${item.id}`);
      toast.success('Loan deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to delete loan');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <BadgeAlert size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Loans</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={load}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loanId && loan) {
    const progress = Number(loan.progress || 0);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Loan Details
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{loan.display_name || 'Loan'} · {loan.type}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-slate-200" onClick={() => navigate('/dashboard/budget/loans')}>Back</Button>
            {canManage ? <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => openPayment(loan)}>Record Payment</Button> : null}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Principal</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(loan.principal, loan.currency || currency)}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Interest</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(loan.total_interest, loan.currency || currency)}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Total</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(loan.total_amount, loan.currency || currency)}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Paid</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(loan.amount_paid, loan.currency || currency)}</p></CardContent></Card>
          <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Remaining</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(loan.balance, loan.currency || currency)}</p></CardContent></Card>
        </div>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Progress</p>
                <p className="text-lg font-semibold text-slate-900">{progress.toFixed(0)}% paid</p>
              </div>
              <Badge className={statusTone(loan.status)}>{loan.status || 'active'}</Badge>
            </div>
            <Progress value={progress} />
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Installments</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-3 pr-4 font-medium">Installment #</th>
                  <th className="py-3 pr-4 font-medium">Due Date</th>
                  <th className="py-3 pr-4 font-medium">Amount Due</th>
                  <th className="py-3 pr-4 font-medium">Amount Paid</th>
                  <th className="py-3 pr-4 font-medium">Balance</th>
                  <th className="py-3 pr-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {installments.map((inst) => (
                  <tr key={inst.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 pr-4 text-slate-700">{inst.installment_number}</td>
                    <td className="py-3 pr-4 text-slate-700">{formatDate(inst.due_date)}</td>
                    <td className="py-3 pr-4 text-slate-700">{money(inst.amount_due, loan.currency || currency)}</td>
                    <td className="py-3 pr-4 text-slate-700">{money(inst.amount_paid, loan.currency || currency)}</td>
                    <td className="py-3 pr-4 text-slate-700">{money(inst.balance, loan.currency || currency)}</td>
                    <td className="py-3 pr-4"><Badge className={installmentTone(inst.status)}>{inst.status || 'pending'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
              <DialogDescription>Apply a payment to the earliest unpaid installment automatically.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Payment Date</Label>
                <Input type="datetime-local" value={paymentForm.payment_date} onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_date: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Notes</Label>
                <Input value={paymentForm.notes} onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-slate-200" onClick={() => setPaymentOpen(false)}>Cancel</Button>
              <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={savePayment} disabled={saving}>Save Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Loans
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Track loans taken and given, installment schedules, and repayments.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={load}><RefreshCw size={16} className="mr-1.5" /> Refresh</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openCreate}><Plus size={16} className="mr-1.5" /> Add Loan</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Total Loans Taken</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{summary.total_loans_taken || 0}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Total Loans Given</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{summary.total_loans_given || 0}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Outstanding Loan Balance</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{money(summary.outstanding_loan_balance || 0, currency)}</p></CardContent></Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl"><CardContent className="p-5"><p className="text-sm text-slate-500">Overdue Installments</p><p className="mt-2 text-2xl font-bold text-slate-900 mono-nums">{summary.overdue_installments || 0}</p></CardContent></Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Loan List</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{loans.length} item(s)</Badge>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-3 pr-4 font-medium">Name</th>
                <th className="py-3 pr-4 font-medium">Type</th>
                <th className="py-3 pr-4 font-medium">Principal</th>
                <th className="py-3 pr-4 font-medium">Total</th>
                <th className="py-3 pr-4 font-medium">Paid</th>
                <th className="py-3 pr-4 font-medium">Balance</th>
                <th className="py-3 pr-4 font-medium">Next Due Date</th>
                <th className="py-3 pr-4 font-medium">Status</th>
                <th className="py-3 pr-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((item) => (
                <tr key={item.id} className="border-b border-slate-50 last:border-0">
                  <td className="py-3 pr-4">
                    <div className="font-medium text-slate-900">{item.display_name || '-'}</div>
                    <div className="text-xs text-slate-500">{item.branch_id ? branches.find((b) => b.id === item.branch_id)?.name || 'Main Branch' : 'Main Branch'}</div>
                  </td>
                  <td className="py-3 pr-4 text-slate-700">{item.type}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.principal, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.total_amount, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.amount_paid, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{money(item.balance, item.currency || currency)}</td>
                  <td className="py-3 pr-4 text-slate-700">{formatDate(item.next_due_date)}</td>
                  <td className="py-3 pr-4"><Badge className={statusTone(item.status)}>{item.status || 'active'}</Badge></td>
                  <td className="py-3 pr-4">
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" className="border-slate-200" onClick={() => navigate(`/dashboard/budget/loans/${item.id}`)}>View</Button>
                      {canManage ? (
                        <>
                          <Button size="sm" variant="outline" className="border-slate-200" onClick={() => openEdit(item)}>Edit</Button>
                          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => openPayment(item)}>Record Payment</Button>
                          <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteLoan(item)}>
                            <Trash2 size={14} />
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
              {loans.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-500">No loans yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Reports</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total interest paid this month</p>
            <p className="text-xl font-bold text-slate-900 mt-2">{money(reports.total_interest_paid_this_month || 0, currency)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Total interest earned</p>
            <p className="text-xl font-bold text-slate-900 mt-2">{money(reports.total_interest_earned || 0, currency)}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-4">
            <p className="text-sm text-slate-500">Interest expense vs revenue</p>
            <p className="text-xl font-bold text-slate-900 mt-2">{money(reports.interest_expense_vs_revenue || 0, currency)}</p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Loan' : 'Add Loan'}</DialogTitle>
            <DialogDescription>Track loans taken or given and auto-generate installments.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {!singleBranch ? (
              <div className="grid gap-2">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={(value) => setForm((prev) => ({ ...prev, branch_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {(activeBranches.length ? activeBranches : branches).map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(value) => setForm((prev) => ({ ...prev, type: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taken">Taken</SelectItem>
                    <SelectItem value="given">Given</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Interest Type</Label>
                <Select value={form.interest_type} onValueChange={(value) => setForm((prev) => ({ ...prev, interest_type: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="reducing">Reducing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Lender Name</Label>
                <Input value={form.lender_name} onChange={(e) => setForm((prev) => ({ ...prev, lender_name: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Borrower Name</Label>
                <Input value={form.borrower_name} onChange={(e) => setForm((prev) => ({ ...prev, borrower_name: e.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Principal</Label>
                <Input type="number" value={form.principal} onChange={(e) => setForm((prev) => ({ ...prev, principal: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Interest Rate (%)</Label>
                <Input type="number" value={form.interest_rate} onChange={(e) => setForm((prev) => ({ ...prev, interest_rate: e.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Duration (months)</Label>
                <Input type="number" value={form.duration_in_months} onChange={(e) => setForm((prev) => ({ ...prev, duration_in_months: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Payment Frequency</Label>
                <Select value={form.payment_frequency} onValueChange={(value) => setForm((prev) => ({ ...prev, payment_frequency: value }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Due Date (optional)</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm((prev) => ({ ...prev, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Collateral Description</Label>
                <Input value={form.collateral_description} onChange={(e) => setForm((prev) => ({ ...prev, collateral_description: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Collateral Value</Label>
                <Input type="number" value={form.collateral_value} onChange={(e) => setForm((prev) => ({ ...prev, collateral_value: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveLoan} disabled={saving}>
              {saving ? 'Saving...' : 'Save Loan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Loan Payment</DialogTitle>
            <DialogDescription>Apply payment to the earliest unpaid installment automatically.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Amount</Label>
              <Input type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Payment Date</Label>
              <Input type="datetime-local" value={paymentForm.payment_date} onChange={(e) => setPaymentForm((prev) => ({ ...prev, payment_date: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Input value={paymentForm.notes} onChange={(e) => setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setPaymentOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={savePayment} disabled={saving}>
              {saving ? 'Saving...' : 'Save Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
